// NATIVE 
import * as React from 'react';



// INSTALLED 
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import MenuItem from '@mui/material/MenuItem';
import TableRow from '@mui/material/TableRow';
import EditIcon from '@mui/icons-material/Edit';
import TableBody from '@mui/material/TableBody';
import TableHead from '@mui/material/TableHead';
import DeleteIcon from '@mui/icons-material/Delete';
import Menu, { MenuProps } from '@mui/material/Menu';
import { styled, alpha  } from '@mui/material/styles';
import { useDispatch, useSelector } from 'react-redux';
import TableContainer from '@mui/material/TableContainer';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Box, Card, Chip, Pagination, TablePagination } from '@mui/material';




// CUSTOM 
import { SnackAlert } from '../SnackAlert';
import { paddingCardsTables, timeToClosedToast } from '../../commons';
import MultipleSnackbars from '../MultipleSnackbars';
import { showNumberRowsTable } from '../../commons/utils/values';
import { ModalSessionExpired } from '../generics/ModalSessionExpired';
import { closeSessionGlobal, hasPemissions } from '../../commons/utils';
import { EditDialogParameter } from '../modals/parametros/EditDialogParameter';
import { getParameterByPagination, getParametersStore, storeActiveParametro, storeDesactiveParametro, storeDetailParameter } from '../../store/parameters';





const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    color: theme.palette.secondary.contrastText,
    fontSize: 15,
    fontWeight: 700
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    fontWeight: 500,
    color: '#2e263de6'
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
  // hide last border
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));




/**
 * TableParametros component is responsible for displaying a table of parameters with pagination and various actions.
 * 
 * @component
 * 
 * @returns {JSX.Element} The rendered TableParametros component.
 * 
 * @description
 * This component fetches and displays a list of parameters from the Redux store. It provides functionalities to edit, deactivate, and restore parameters based on user permissions. The component also handles pagination and displays alerts for various actions.
 * 
 * @example
 * <TableParametros />
 * 
 * @requires useDispatch - To dispatch actions to the Redux store.
 * @requires useSelector - To select state from the Redux store.
 * @requires React.useState - To manage local state.
 * @requires React.useEffect - To handle side effects.
 * 
 * @state {Array} dataTableList - The list of parameters to be displayed in the table.
 * @state {Object} pagination - The pagination details for the table.
 * @state {number} page - The current page number for pagination.
 * @state {number} rowsPerPage - The number of rows per page for pagination.
 * @state {number} pagePagination - The current page number for the pagination component.
 * @state {number} itemCountPagination - The total number of pages for the pagination component.
 * @state {Array} msgErrors - The list of error messages.
 * @state {HTMLElement | null} openElem - The currently open element for the menu.
 * @state {HTMLElement | null} anchorEl - The anchor element for the menu.
 * @state {string} message_alert_snack - The message for the alert snackbar.
 * @state {string} type_alert_snack - The type of the alert snackbar (e.g., success, error).
 * @state {boolean} modalDetalleTurno - The state for the detail modal.
 * @state {boolean} modalEditarParameter - The state for the edit parameter modal.
 * @state {boolean} canEdit - Whether the user has permission to edit parameters.
 * @state {boolean} canDelete - Whether the user has permission to delete parameters.
 * @state {boolean} canRestore - Whether the user has permission to restore parameters.
 * @state {Array} messagesAlerts - The list of alert messages.
 * @state {number} timeAlert - The duration for which the alert should be displayed.
 * @state {boolean} modalSessionExpired - The state for the session expired modal.
 * @state {boolean} alert_snack - The state for the alert snackbar.
 * @state {Object} msgSnack - The message and status for the snackbar.
 * 
 * @function handleClose - Closes the menu.
 * @function handleClick - Opens the menu for the specified element.
 * @function getDataParameters - Fetches the list of parameters.
 * @function openDialogEditarParameter - Opens the edit parameter dialog.
 * @function desactiveParameter - Deactivates a parameter.
 * @function restoreParameter - Restores a parameter.
 * @function handleCloseModalEditarParameter - Closes the edit parameter modal.
 * @function handleChangeRowsPerPage - Handles the change in rows per page for pagination.
 * @function handleChangePage - Handles the change in page number for pagination.
 * @function changePaginationPage - Handles the change in page number for the pagination component.
 * @function validateLsPermissions - Validates the user's permissions and displays an alert if they do not have the required permissions.
 * @function validateResponse - Validates the response from the server and displays appropriate alerts.
 * @function checkEdit - Checks if the user has permission to edit parameters.
 * @function checkCanDelete - Checks if the user has permission to delete parameters.
 * @function checkRestore - Checks if the user has permission to restore parameters.
 */
export const TableParametros = () => {
    

    // DISPATCH 
    const dispatch = useDispatch();
  

    // REDUX
    const  {  parametros_list, parametro }:any  = useSelector( (state:any) => state.parametros );


    // USE STATE 
    const [dataTableList, setDataTable] = React.useState([]);
    const [pagination, setPagination] = React.useState<any>({
        "page": 1,
        "take": 1,
        "itemCount": 1,
        "pageCount": 1,
        "hasPreviousPage": false,
        "hasNextPage": false
    });

    

    // USE STATE 
    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(showNumberRowsTable);
    const [pagePagination, setPagePagination] = React.useState(1);
    const [itemCountPagination, setItemCountPagination] = React.useState(1);


    const [msgErrors, setMsgErrors] = React.useState([]);

    const [openElem, setOpenElem] = React.useState(null);
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

   
    const [message_alert_snack, setMessageAlertSnack] = React.useState('');
    const [type_alert_snack, setTypeAlertSnack] = React.useState('success');



    const [modalDetalleTurno, setModalDetalleTurno] = React.useState(false);
    const [modalEditarParameter, setModalEditarParameter] = React.useState(false);

  
    const [canEdit, setCanEdit]     = React.useState<boolean>(false);
    const [canDelete, setCanDelete]     = React.useState<boolean>(false);
    const [canRestore, setCanRestore]     = React.useState<boolean>(false);


    const [messagesAlerts, setMessagesAlerts] = React.useState<any>([]);


    const [timeAlert, setTimeAlert] = React.useState(timeToClosedToast);
    const [modalSessionExpired, setModalSessionExpired] = React.useState<boolean>(false);

    const [alert_snack, setAlertSnack] = React.useState(false);
    const [msgSnack, setMsgSnack] = React.useState({
        msg: '',
        status: 'success'
    });
    
    


    // USE EFFECT 
    React.useEffect(() => {
      
      if ( parametros_list.data && parametros_list.data.length > 0 ) {

        setDataTable(parametros_list.data);
        setPagination({
          "page": parametros_list.meta.page,
          "take": parametros_list.meta.take,
          "itemCount": parametros_list.meta.itemCount,
          "pageCount": parametros_list.meta.pageCount,
          "hasPreviousPage": parametros_list.meta.hasPreviousPage,
          "hasNextPage": parametros_list.meta.hasNextPage
        });
        setItemCountPagination(parametros_list.meta.pageCount);
        
      }
      

    }, [parametros_list]);


    // HERE LOAD THE DATA NECESARY FOR CREATE PARAMETERS 
    React.useEffect( () => {
      getDataParameters();
    }, []);


    React.useEffect(() => {
      checkEdit()
    }, []);
  
    React.useEffect(() => {
        checkCanDelete()
    }, []);
  
    React.useEffect(() => {
      checkRestore()
    }, []);


    // CONST STYLES 
    const StyledMenu = styled((props: MenuProps) => (
      <Menu
        elevation={0}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        {...props}
      />
    ))(({ theme }) => ({
      '& .MuiPaper-root': {
        borderRadius: 6,
        marginTop: theme.spacing(1),
        minWidth: 180,
        color:
          theme.palette.mode === 'light' ? 'rgb(55, 65, 81)' : theme.palette.grey[300],
        boxShadow:
          'rgb(255, 255, 255) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px',
        '& .MuiMenu-list': {
          padding: '7px 0',
        },
        '& .MuiMenuItem-root': {
          '& .MuiSvgIcon-root': {
            fontSize: 18,
            color: theme.palette.text.secondary,
            marginRight: theme.spacing(1.5),
          },
          '&:active': {
            backgroundColor: alpha(
              theme.palette.primary.main,
              theme.palette.action.selectedOpacity,
            ),
          },
        },
      },
    }));
    
    
    const Div = styled('div')(({ theme }) => ({
      ...theme.typography.button,
      backgroundColor: 'wheat',
      padding: theme.spacing(8),
      textAlign: 'center'
    }));


    



    // CONST VALUE
    const open = Boolean(anchorEl);
  

    // CONST FUNCTIONS 
    const handleClose = () => {
      setAnchorEl(null);
      setOpenElem(null);
    };

  
    const handleClick = (elem:any) => (event:any) => {
      setAnchorEl(event.currentTarget);
      setOpenElem(elem);
    };



    const getDataParameters = async() => {
    
    
    
      // modulo.parametros                             nombre modulo     sub modulo    permission
      const havePermissions = await hasPemissions('modulo.thumano', 'modulo.parametros', 'view');


      if( !havePermissions ){


        await validateLsPermissions('No tienes pemisos para (listar) parametros.');

    
         return false;

        
      }

      
      const response = await dispatch( getParametersStore() );


      await validateResponse(response, ' (Listar Parametro) ');


    }
  
    
  
    const openDialogEditarParameter = async(parameterId:string) => {



      setAnchorEl(null);
      setOpenElem(null);

      const havePermissions = await hasPemissions('modulo.thumano', 'modulo.parametros', 'view');


        if( !havePermissions ){
    
   
         await validateLsPermissions('No tienes pemisos para (Ver) parametro.');

    
         return false;
                                                               
            
        }


   
      const response = await dispatch( storeDetailParameter(parameterId) );
  

      const result = await validateResponse(response, ' (Ver parametro) ');


      if (!result) {
         return false;
      }

      
      setModalEditarParameter(true);

    }
  
  
    const desactiveParameter = async(parametroId:string) => {

      
      setAnchorEl(null);
      setOpenElem(null);


      const havePermissions = await hasPemissions('modulo.thumano', 'modulo.parametros', 'delete');


        if( !havePermissions ){
    
   
         await validateLsPermissions('No tienes pemisos para (Desactivar) parametro.');

    
         return false;
                                                               
            
        }

        
      const response = await dispatch( storeDesactiveParametro(parametroId) );


      await validateResponse(response, ' (Desactivar parametro) ');


      

        
    }
  
  
    const restoreParameter = async(parametroId:string) => {


      setAnchorEl(null);
      setOpenElem(null);

      const havePermissions = await hasPemissions('modulo.thumano', 'modulo.parametros', 'restore');


        if( !havePermissions ){
    
          await validateLsPermissions('No tienes pemisos para (Activar) parametro.');

    
          return false;
  
        }
  
  
      const response = await dispatch( storeActiveParametro(parametroId) );


      await validateResponse(response, ' (Activar parametro) ');
  
      
  
    }
  
    
    const handleCloseModalEditarParameter = () => {
         setModalEditarParameter(false);
    }
  
   
  
    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {

      setRowsPerPage(+event.target.value);

      setPage(0);

    };



    const handleChangePage = (event: unknown, newPage: number) => {

          setPage(newPage);

    };


    const changePaginationPage = async(event: React.ChangeEvent<unknown>, value: number) => {
        
        setPagePagination(value)
    
        const response =  await dispatch( getParameterByPagination(value) );
        

        await validateResponse(response, ' (Listar Parametro) ');
        
    
    };



    const validateLsPermissions = async(message:string) => {

      const errors:any = {
        msg: message,
        status: 'error'
      };
    
      await setMsgSnack(errors);
      await setAlertSnack(true);
    
    
    }


    const validateResponse = async(response:any, textActionConcat: string) => {
        
        
          if (  response.code == 401  ) {
            await localStorage.removeItem('lastSelectedQypht');
            await closeSessionGlobal();
            setModalSessionExpired(true);
            return false;
          }
  
        
          // 403 NO TIENE PERMISSO PARA REALIZAR ESA ACCION 
          if (!response.ok && response.code == 403 ) { 
  
          
            const errors:any = {
              msg: response.errors[0]+' '+textActionConcat,
              status: 'error'
            };
          
            await setMsgSnack(errors);
            await setAlertSnack(true);
  
          
            return false;
            
          }
  
          
          // 400 ERROR EN DATOS ENVIADOS MAYORMENTE PROPIEDAD INVALIDA O MAL FORMATEADO 
          if (!response.ok && response.code == 400 ) { 
  
            const errors:any = [];
  
            response.errors.forEach( (element:any) => {
                
                errors.push({
                  type: 'error',
                  text: element
                })
            });
  
              
            setMessagesAlerts(errors);
            return false;
  
          }
  
          return true;
        
    }



    const checkEdit = async() => {
                
        
      const hasPermissionsEdit = await hasPemissions('modulo.thumano', 'modulo.parametros', 'edit');
    
      if ( hasPermissionsEdit ) {
            setCanEdit(true);
      }

    }
    
    
    const checkCanDelete = async() => {
        
        
        const havePermissions = await hasPemissions('modulo.thumano', 'modulo.parametros', 'delete');
        
        if ( havePermissions ) {
              setCanDelete(true);
        }
  
    }
    
    
    const checkRestore = async() => {
        
        const havePermissions = await hasPemissions('modulo.thumano', 'modulo.parametros', 'restore');
          
        if ( havePermissions ) {
              setCanRestore(true);
        }
  
    }

    
    return (
  
      <>
  
        <ModalSessionExpired  open={modalSessionExpired}  />
        <MultipleSnackbars messagesList={messagesAlerts} position={"bottom-left"} autoClose={timeAlert} />
        <SnackAlert open={alert_snack} onclick={setAlertSnack} text={msgSnack.msg} severity={msgSnack.status}/>

        <EditDialogParameter  open={modalEditarParameter} handleCloseModalEditarParameter={handleCloseModalEditarParameter} />

        
  
         {
              
              dataTableList.length > 0
                  
                ?
                <>

                

                <Card
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    p: 3,
                    borderRadius: 2,
                    boxShadow: 3,
                    bgcolor: 'background.paper',
                    marginTop: paddingCardsTables
                  }}
                >

                <TableContainer component={Paper}>
                  <Table sx={{ minWidth: 700 }} aria-label="customized table">
                    <TableHead>
                      <TableRow>
                        <StyledTableCell>#</StyledTableCell>
                        <StyledTableCell >Minutos llegada tarde</StyledTableCell>
                        <StyledTableCell >Minutos salida tarde</StyledTableCell>
                        <StyledTableCell >Salario mínimo</StyledTableCell>
                        <StyledTableCell >Aux. transporte</StyledTableCell>
                        <StyledTableCell >Interés Cesantías</StyledTableCell>
                        <StyledTableCell >Año</StyledTableCell>
                        <StyledTableCell >Estado</StyledTableCell>
                        <StyledTableCell >Acciones</StyledTableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {dataTableList
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((parametro:any, index:any) => (
                        <StyledTableRow key={index+1}>
                          <StyledTableCell component="th" scope="row">
                            {index+1}
                          </StyledTableCell>
                          <StyledTableCell component="th" scope="row">
                            {parametro.llegadas_tarde}
                          </StyledTableCell>
                          <StyledTableCell >
                            {parametro.salidas_tarde}
                          </StyledTableCell>

                          <StyledTableCell >
                            {parametro.salario_minimo}
                          </StyledTableCell>
                          <StyledTableCell >
                            {parametro.auxilio_transporte}
                          </StyledTableCell>
                          <StyledTableCell >
                            {parametro.interes_cesantias}
                          </StyledTableCell>
                          <StyledTableCell >
                            {parametro.year}
                          </StyledTableCell>
                          <StyledTableCell >
                            {
                              parametro.status ? <Chip size='small' label="Activo" sx={{ color: '#5DCC0A', background: '#E4F6D6', fontWeight: 700 }} /> : <Chip size='small' label="Inactivo" sx={{ color: '#FF4C51', background: '#FFE2E3', fontWeight: 700 }} />
                            }
                          </StyledTableCell>
                          <StyledTableCell  >
                                
                          
                          {
                          
                              <div> 
                                  <Button
                                    id={`demo-customized-button-${index+1}`}
                                    aria-controls={open ? `demo-customized-menu-${index+1}` : undefined}
                                    aria-haspopup="true"
                                    aria-expanded={open ? 'true' : undefined}
                                    variant="text"
                                    disableElevation
                                    onClick={handleClick(parametro)}
                                    endIcon={<KeyboardArrowDownIcon />}
                                  >
                                    Acciones
                                  </Button>
                                  <StyledMenu
                                    id={`demo-customized-menu-${index+1}`}
                                    MenuListProps={{
                                      'aria-labelledby': `demo-customized-button-${index+1}`,
                                    }}
                                    anchorEl={anchorEl}
                                    open={openElem === parametro}
                                    onClose={handleClose}
                                  >
                                    
                                    {
                                      (parametro.status) 
                                        ? <div>
                                          {/* <MenuItem onClick={ () => openDialogDetalle(parametro.id) }  disableRipple>
 
                                          <VisibilityIcon />
                                            Detalle 
                                          </MenuItem> */}

                                          {
                                             canEdit && 
                                                <>
                                                   <Divider sx={{ my: 0.5 }} />
                                                   <MenuItem onClick={ () => openDialogEditarParameter(parametro.id) }  disableRipple>
                                                     <EditIcon />
                                                       Editar
                                                  </MenuItem> 
                                                </>
                                          }
                                        </div>
                                        :
                                        ''
                                    }
                                    
                                    {
                                      (parametro.status) 
                                      ?
                                        <div>
                                          {
                                             canDelete && 
                                                <>
                                                    <Divider sx={{ my: 0.5 }} />
                                                    <MenuItem onClick={ () => desactiveParameter(parametro.id) } disableRipple>
                                                      <DeleteIcon />
                                                      Desactivar
                                                    </MenuItem>
                                                </>
                                          }
                                        </div>
                                      :
                                      <div>
                                         {
                                            canRestore && 
                                                 <>
                                                    <Divider sx={{ my: 0.5 }} />
                                                    <MenuItem onClick={ () => restoreParameter(parametro.id) } disableRipple>
                                                      <DeleteIcon />
                                                      Activar
                                                    </MenuItem>
                                                 </>
                                         }
                                      </div>
                                      
                                    }
                                  </StyledMenu>
                                </div>
                      
                          }
          
          
          
          
                          </StyledTableCell>
                        </StyledTableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                </Card>
                <TablePagination
                  rowsPerPageOptions={[5, 10]}
                  labelRowsPerPage={'Filas por pagina'}
                  labelDisplayedRows={({ from, to, count }) => ` ${from}-${to} de  ${count} `}
                  component="div"
                  count={pagination.itemCount}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={ handleChangePage }
                  onRowsPerPageChange={ handleChangeRowsPerPage }
                />

                <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        marginTop: 2,
                        marginBottom: 2
                      }}
                    >
                    <Pagination sx={{ justifyContent: 'center' }} color="primary" count={itemCountPagination} page={pagePagination} onChange={ changePaginationPage} showFirstButton showLastButton />
                </Box>
                </>
                
                :
                <Div>{"No hay parametros disponibles..."}</Div>
         }
  
  
         
         
      </>
      
    );
}
